/**
 * Client-side media pipeline (issue #11):
 *   HEIC → JPEG, downscale + re-encode to ~2 MB JPEG, optional caption burn-in.
 * Videos skip the pipeline entirely and upload as-is.
 */

import { composeStripText } from "./caption";
import { canvasToJpegBlob, decodeImage, releaseSource, sourceSize } from "./decode";
import { drawCaption } from "./burnin";
import {
  DEFAULT_CAPTION_STYLE,
  resolveFrameAspectRatio,
  type CaptionStyle
} from "../types";
import { heicToJpeg } from "./heic";
import {
  computeFramefillDimensions,
  computeTargetDimensions,
  coverCrop,
  ENCODE_ATTEMPTS,
  isAcceptableSize,
  TARGET_IMAGE_BYTES,
  uploadFileName
} from "./resize";
import { videoContentType } from "./validation";

export interface PipelineOptions {
  /** Burn caption strip into photos (config.captionBurnIn && caption present). */
  burnIn: boolean;
  caption: string;
  guestName: string;
  /** "Now" for the month/year attribution — injectable for determinism. */
  date: Date;
  /** Per-property caption style. Absent → framesafe default. */
  captionStyle?: CaptionStyle;
  /**
   * Per-property target frame aspect ('W:H'). Only used by the 'framefill'
   * style, which composes the output at this shape. Absent/invalid → 4:3.
   */
  frameAspect?: string;
}

export interface ProcessedFile {
  blob: Blob;
  /** Generated upload name (never the user's filename). */
  name: string;
  type: string;
  kind: "image" | "video";
}

/**
 * Run one selected file through the pipeline.
 * `index` determines the generated upload filename (1.jpg, 2.mp4, ...).
 * `preConvertedJpeg` lets the caller reuse a HEIC→JPEG conversion it already
 * performed (e.g. for the thumbnail/preview) instead of converting twice.
 */
export async function processFile(
  file: File,
  kind: "image" | "heic" | "video",
  index: number,
  options: PipelineOptions,
  preConvertedJpeg?: Blob
): Promise<ProcessedFile> {
  if (kind === "video") {
    return {
      blob: file,
      name: uploadFileName(index, "video", file.name),
      // Report the API-allowed content type; corrects .mov with empty MIME (WI #44).
      type: videoContentType(file.name, file.type),
      kind: "video"
    };
  }

  const sourceBlob: Blob =
    kind === "heic" ? (preConvertedJpeg ?? (await heicToJpeg(file))) : file;
  const blob = await renderToJpeg(sourceBlob, options);
  return {
    blob,
    name: uploadFileName(index, "image", file.name),
    type: "image/jpeg",
    kind: "image"
  };
}

/** Decode → downscale → optional caption strip → quality-stepped JPEG encode. */
async function renderToJpeg(sourceBlob: Blob, options: PipelineOptions): Promise<Blob> {
  const source = await decodeImage(sourceBlob);
  try {
    const { width, height } = sourceSize(source);
    const strip = options.burnIn
      ? composeStripText(options.caption, options.guestName, options.date)
      : null;
    const style = options.captionStyle ?? DEFAULT_CAPTION_STYLE;

    // 'framefill' composes AT the frame's aspect ratio (photo cover-cropped to
    // fill it) so the frame can't crop the caption — but only when there IS a
    // caption to protect; otherwise fall back to the photo's own aspect.
    const framefill = style === "framefill" && strip !== null;
    const targetRatio = resolveFrameAspectRatio(options.frameAspect);
    const base = framefill
      ? computeFramefillDimensions(width, height, targetRatio)
      : computeTargetDimensions(width, height);

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D not available");

    let best: Blob | null = null;
    for (let i = 0; i < ENCODE_ATTEMPTS.length; i++) {
      const attempt = ENCODE_ATTEMPTS[i]!;
      canvas.width = Math.max(1, Math.round(base.width * attempt.scale));
      canvas.height = Math.max(1, Math.round(base.height * attempt.scale));
      if (framefill) {
        const crop = coverCrop(width, height, canvas.width, canvas.height);
        ctx.drawImage(
          source,
          crop.sx,
          crop.sy,
          crop.sw,
          crop.sh,
          0,
          0,
          canvas.width,
          canvas.height
        );
      } else {
        ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
      }
      if (strip) {
        drawCaption(ctx, canvas.width, canvas.height, strip, style);
      }

      const blob = await canvasToJpegBlob(canvas, attempt.quality);
      if (best === null || blob.size < best.size) best = blob;
      if (isAcceptableSize(blob.size, i, TARGET_IMAGE_BYTES)) {
        return blob.size <= TARGET_IMAGE_BYTES ? blob : best;
      }
    }
    // Unreachable (the last attempt always accepts), but keeps TS honest.
    return best!;
  } finally {
    releaseSource(source);
  }
}
